import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router";
import { useMediaQuery } from "react-responsive";

import Header from "./component/common/header";
import ReserveInput from "./component/retrieve/reserveInput";
import ContactInfoInput from "./component/retrieve/contactInfoInput";
import ReserveList from "./component/retrieve/reserveList";
import ResultNotify from "./component/retrieve/resultNotify";
import ActionConfirmation from "./component/common/actionConfirmation";
import KioskIdleModal from "./component/common/KioskIdleModal";
import LockerOpenResultModal from "./component/common/LockerOpenResultModal";

import { RetrieveContainer } from "./component/styled/selfService";
import { useDisableScroll } from "../../hooks/useDisableScroll";
import { requestCardApprove } from "../../utils/nvcat";
import { useKioskProtection } from "./hooks/useKioskProtection";
import { useKioskIdleTimer } from "./hooks/useKioskIdleTimer";

import { requestOpenLockCommands } from "../../utils/lockerCommand";

const Retrieve = () => {
  useDisableScroll();
  useKioskProtection({ enabled: true });

  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  /**
   * step
   * 0 전화번호 입력
   * 1 비밀번호 입력
   * 2 예약 선택
   * 3 보관함 선택
   * 4 추가금 결제
   * 5 찾기 완료
   */
  const [active, setActive] = useState(0);

  const [formData, setFormData] = useState({
    tel: "010",
    password: "",
  });

  const [reserveList, setReserveList] = useState([]);
  const [selectedReserve, setSelectedReserve] = useState(null);
  const [details, setDetails] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [pickupRequestRows, setPickupRequestRows] = useState([]);
  const [addPayInfo, setAddPayInfo] = useState(null);
  const [isPayingAddPay, setIsPayingAddPay] = useState(false);
  const [openedStorages, setOpenedStorages] = useState([]);
  const [isLockerResultOpen, setIsLockerResultOpen] = useState(false);

  const transformValue = active * -100;

  const totalAddPay = useMemo(() => {
    return pickupRequestRows.reduce((sum, row) => {
      if (row?.addPayTof) return sum;
      return sum + Number(row?.addPay || 0);
    }, 0);
  }, [pickupRequestRows]);

  const resetRetrieveFlowAll = () => {
    setActive(0);
    setFormData({
      tel: "010",
      password: "",
    });
    setReserveList([]);
    setSelectedReserve(null);
    setDetails([]);
    setSelectedItems([]);
    setPickupRequestRows([]);
    setAddPayInfo(null);
    setIsPayingAddPay(false);
    setOpenedStorages([]);
    setIsLockerResultOpen(false);
  };

  const resetReserveFlow = () => {
    setReserveList([]);
    setSelectedReserve(null);
    setDetails([]);
    setSelectedItems([]);
    setPickupRequestRows([]);
    setAddPayInfo(null);
    setIsPayingAddPay(false);
  };

  const resetDetailFlow = () => {
    setSelectedReserve(null);
    setDetails([]);
    setSelectedItems([]);
    setPickupRequestRows([]);
    setAddPayInfo(null);
    setIsPayingAddPay(false);
  };

  const { isWarningOpen, countdown, closeWarning } = useKioskIdleTimer({
    enabled: !isPayingAddPay && active !== 5,
    timeout: active === 4 ? 120000 : 90000,
    warningDuration: 15000,
    onTimeout: () => {
      resetRetrieveFlowAll();
      navigate("/self-service", { replace: true });
    },
  });

  useEffect(() => {
    if (active !== 5 || isLockerResultOpen) return;

    const timer = setTimeout(() => {
      resetRetrieveFlowAll();
      navigate("/self-service", { replace: true });
    }, 15000);

    return () => clearTimeout(timer);
  }, [active, isLockerResultOpen, navigate]);

  const handleChangeInput = (key, value) => {
    if (key === "tel") {
      let next = value.replace(/\D/g, "");

      if (!next.startsWith("010")) {
        next = `010${next.replace(/^010/, "")}`;
      }

      next = next.slice(0, 11);

      setFormData((prev) => ({
        ...prev,
        tel: next,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const stepCheck = () => {
    if (active === 0) {
      const tel = formData.tel.replace(/\D/g, "");
      const telRegex = /^010[0-9]{8}$/;

      if (!telRegex.test(tel)) {
        toast.error("유효한 전화번호를 입력해주세요.");
        return false;
      }
    }

    if (active === 1) {
      const password = formData.password?.trim();

      if (!/^[0-9]{4}$/.test(password)) {
        toast.error("비밀번호는 숫자 4자리입니다.");
        return false;
      }
    }

    if (active === 2) {
      if (!selectedReserve) {
        toast.error("예약 내역을 선택해주세요.");
        return false;
      }
    }

    if (active === 3) {
      if (!selectedItems.length) {
        toast.error("찾을 보관함을 선택해주세요.");
        return false;
      }

      const reserveIdSet = new Set(selectedItems.map((item) => item.reserveId));
      if (reserveIdSet.size > 1) {
        toast.error("같은 예약 건만 함께 선택할 수 있습니다.");
        return false;
      }
    }

    return true;
  };

  const getReserveList = async () => {
    const tel = formData.tel.replace(/\D/g, "");
    const pwd = formData.password.trim();

    try {
      const res = await axios.post("/api/kiosk/find", {
        phone: tel,
        pwd,
      });

      const list = Array.isArray(res.data) ? res.data : [];

      if (!list.length) {
        toast.error("일치하는 예약 정보를 찾을 수 없습니다.");
        return;
      }

      setReserveList(list);
      setSelectedReserve(null);
      setDetails([]);
      setSelectedItems([]);
      setPickupRequestRows([]);
      setAddPayInfo(null);
      setActive(2);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "예약 정보를 조회하지 못했습니다."
      );
    }
  };

  const getReserveDetail = async (reserve) => {
    try {
      const reserveId = reserve?.reserveId ?? reserve?.id;
      const point = reserve?.point ?? "bank";

      if (!reserveId) {
        toast.error("예약 정보가 올바르지 않습니다.");
        return;
      }

      const res = await axios.post("/api/reserve-user-detail", {
        id: reserveId,
        point,
      });

      const list = Array.isArray(res.data) ? res.data : [];

      if (!list.length) {
        toast.error("보관함 상세 정보가 없습니다.");
        return;
      }

      setDetails(list);
      setSelectedItems([]);
      setPickupRequestRows([]);
      setAddPayInfo(null);
      setActive(3);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "보관함 상세 정보를 조회하지 못했습니다."
      );
    }
  };

  const handleSelectReserve = async (reserve) => {
    setSelectedReserve(reserve);
    await getReserveDetail(reserve);
  };

  const pickupComplete = async ({ reserveId, historyIds, point }) => {
    try {
      const res = await axios.post("/api/pickup", {
        point,
        reserveId,
        historyIds,
      });

      console.log("pickup response =", res.data);

      const openedStorages = Array.isArray(res.data)
        ? res.data
            .filter((item) => item?.storageId)
            .map((item) => ({
              id: item.id,
              reserveId: item.reserveId,
              storageId: item.storageId,
              type: item.type,
              reservationStatus: item.reservationStatus,
              point: item.point ?? point,
            }))
        : [];

      await requestOpenLockCommands({
        point: point ?? "bank",
        storages: openedStorages,
        pulseMs: 400,
        requestedBy: "kiosk",
        requestNote: "키오스크 찾기 완료 후 문 열기",
        keyPrefix: "kiosk-pickup-open",
        reserveId,
      });

      setOpenedStorages(openedStorages);
      setActive(5);
      setIsLockerResultOpen(true);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "픽업 처리 중 오류가 발생했습니다."
      );
    }
  };

  const requestPickup = async () => {
    try {
      const reserveId = selectedItems[0]?.reserveId;
      const point = selectedItems[0]?.point ?? selectedReserve?.point ?? "bank";
      const historyIds = selectedItems.map((item) => item.id);

      if (!reserveId || !historyIds.length) {
        toast.error("선택된 보관 정보가 올바르지 않습니다.");
        return;
      }

      const res = await axios.post("/api/pickup-request", {
        point,
        reserveId,
        historyIds,
      });

      const list = Array.isArray(res.data) ? res.data : [];
      console.log("pickup-request response =", list);

      if (!list.length) {
        toast.error("픽업 요청 결과가 없습니다.");
        return;
      }

      setPickupRequestRows(list);

      const needAddPay = list.some(
        (row) => Number(row?.addPay || 0) > 0 && !row?.addPayTof
      );

      if (needAddPay) {
        setAddPayInfo({
          reserveId,
          historyIds,
          point,
        });
        setActive(4);
        return;
      }

      await pickupComplete({
        reserveId,
        historyIds,
        point,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data ||
          "픽업 요청 처리 중 오류가 발생했습니다."
      );
    }
  };

  const confirmAddPay = async () => {
    try {
      if (isPayingAddPay) return;

      if (!addPayInfo) {
        toast.error("추가금 정보가 없습니다.");
        return;
      }

      const amount = pickupRequestRows.reduce((sum, row) => {
        if (row?.addPayTof) return sum;
        return sum + Number(row?.addPay || 0);
      }, 0);

      if (amount <= 0) {
        toast.error("추가금 금액이 올바르지 않습니다.");
        return;
      }

      setIsPayingAddPay(true);

      const paymentResult = await requestCardApprove({
        amount,
        tax: "0",
        bongsa: "0",
        halbu: "00",
      });

      console.log("[RETRIEVE] add-pay paymentResult:", paymentResult);

      const { reserveId, historyIds, point } = addPayInfo;

      const res = await axios.post("/api/add-pay", {
        point,
        reserveId,
        historyIds,
        addPayType: 1,
        orderId: paymentResult.approvalNo || "",
        paymentKey: paymentResult.catId || "",
      });

      console.log("add-pay response =", res.data);

      await pickupComplete({
        reserveId,
        historyIds,
        point,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data ||
          "추가금 결제 처리 중 오류가 발생했습니다."
      );
    } finally {
      setIsPayingAddPay(false);
    }
  };

  const handleStep3Confirm = async () => {
    if (!selectedItems.length) {
      toast.error("찾을 보관함을 선택해주세요.");
      return;
    }

    const reserveId = selectedItems[0]?.reserveId;
    const point = selectedItems[0]?.point ?? selectedReserve?.point ?? "bank";
    const historyIds = selectedItems.map((item) => item.id);

    const statuses = [...new Set(selectedItems.map((item) => item.reservationStatus))];

    if (statuses.length > 1) {
      toast.error("같은 상태의 보관함만 함께 선택할 수 있습니다.");
      return;
    }

    const status = statuses[0];

    if (status === "COMPLETED") {
      await requestPickup();
      return;
    }

    if (status === "READY") {
      const unpaidRows = selectedItems.filter(
        (item) => Number(item?.addPay || 0) > 0 && !item?.addPayTof
      );

      if (unpaidRows.length > 0) {
        setPickupRequestRows(unpaidRows);
        setAddPayInfo({
          reserveId,
          historyIds: unpaidRows.map((item) => item.id),
          point,
        });
        setActive(4);
        return;
      }

      await pickupComplete({
        reserveId,
        historyIds,
        point,
      });
      return;
    }

    toast.error("현재 상태에서는 진행할 수 없습니다.");
  };

  const determineStepAction = async (isNext = false) => {
    if (isPayingAddPay) {
      toast.error("결제 진행 중에는 이동할 수 없습니다.");
      return;
    }

    if (active === 5) return;

    if (isNext) {
      if (!stepCheck()) return;

      if (active === 1) {
        await getReserveList();
        return;
      }

      if (active === 2) {
        await getReserveDetail(selectedReserve);
        return;
      }

      if (active === 3) {
        await handleStep3Confirm();
        return;
      }

      if (active === 4) {
        await confirmAddPay();
        return;
      }

      setActive((prev) => prev + 1);
      return;
    }

    if (active === 0) return;

    if (active === 4) {
      setPickupRequestRows([]);
      setAddPayInfo(null);
      setActive(3);
      return;
    }

    if (active === 3) {
      setSelectedItems([]);
      setPickupRequestRows([]);
      setAddPayInfo(null);
      setActive(2);
      return;
    }

    if (active === 2) {
      resetDetailFlow();
      setActive(1);
      return;
    }

    if (active === 1) {
      resetReserveFlow();
      setActive(0);
      return;
    }

    setActive((prev) => prev - 1);
  };

  const pageTitle =
    active === 0
      ? "전화번호 입력"
      : active === 1
      ? "비밀번호 입력"
      : active === 2
      ? "예약 선택"
      : active === 3
      ? "보관함 선택"
      : active === 4
      ? "추가금 결제"
      : "찾기 완료";

  return (
    <RetrieveContainer>
      <Header title={pageTitle} handleBackButton={determineStepAction} />

      <div
        className="content"
        style={{ height: active < 5 ? `calc(100% - ${isMobile ? 50 : 86}px)` : "100%" }}
      >
        <div className="slideWrapper">
          <ReserveInput
            transformValue={transformValue}
            formData={formData}
            handleChangeInput={handleChangeInput}
            determineStepAction={determineStepAction}
          />

          <ContactInfoInput
            transformValue={transformValue}
            formData={formData}
            handleChangeInput={handleChangeInput}
            determineStepAction={determineStepAction}
          />

          <ReserveList
            transformValue={transformValue}
            reserves={reserveList}
            selectedReserve={selectedReserve}
            setSelectedReserve={setSelectedReserve}
            onSelectReserve={handleSelectReserve}
            determineStepAction={determineStepAction}
          />

          <ResultNotify
            transformValue={transformValue}
            details={details}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            determineStepAction={determineStepAction}
          />

          <ActionConfirmation
            transformValue={transformValue}
            title={
              totalAddPay > 0
                ? `추가금 ${totalAddPay.toLocaleString()}원 결제`
                : "추가금 결제 진행"
            }
            explanation={[
              "추가금이 확인되었습니다.",
              isPayingAddPay
                ? "카드 결제를 진행하고 있습니다."
                : "확인 버튼을 누르면 카드 추가금 결제를 진행합니다.",
            ]}
            onConfirm={confirmAddPay}
          />

          <ActionConfirmation
            transformValue={transformValue}
            title="찾기 완료"
            explanation={[
              "선택한 보관함이 열렸습니다.",
              "짐을 꺼낸 뒤 문을 닫아주세요.",
            ]}
            onConfirm={() => {
              resetRetrieveFlowAll();
              navigate("/self-service", { replace: true });
            }}
          />
        </div>
      </div>

      <KioskIdleModal
        open={isWarningOpen}
        countdown={countdown}
        onContinue={closeWarning}
        onExit={() => {
          resetRetrieveFlowAll();
          navigate("/self-service", { replace: true });
        }}
      />

      <LockerOpenResultModal
        open={isLockerResultOpen}
        storages={openedStorages}
        action="retrieve"
        onConfirm={() => {
          resetRetrieveFlowAll();
          navigate("/self-service", { replace: true });
        }}
      />
    </RetrieveContainer>
  );
};

export default Retrieve;
